/// <amd-module name="Reporting/CFDReport/CfdReport" />
import * as React from "react";
import { BoardsReport, BoardsReportState } from "Reporting/BoardsCommon/BoardsReport";
import { getBoardsReportUrl } from "Reporting/BoardsCommon/BoardsRoutingHelper";
import { getInfoIcon, hasUserSeenBubble, ReportGuidedTourUtility } from "Reporting/BoardsCommon/ReportGuidedTourUtility";
import { resetQueryCache } from "Reporting/BoardsCommon/ReportQueryHelper";
import { publishEmptyReportTelemetry, publishReportTelemetry } from "Reporting/BoardsCommon/ReportTelemetry";
import { ResetElement } from "Reporting/BoardsCommon/ResetElement";
import * as CommonResources from "Reporting/BoardsCommon/Resources";
import { UnmaterializedBoardMessage } from "Reporting/BoardsCommon/UnmaterializedBoardMessage";
import { Board, getBoard } from "Reporting/CFDReport/Pickers/BoardsQuery";
import { Column, ColumnPicker, getColumns } from "Reporting/CFDReport/Pickers/ColumnPicker";
import { customRollingPeriodId, getDaysItem, getDaysItems } from "Reporting/CFDReport/Pickers/DaysPickerOptions";
import { BoardSwimlane, getSwimlanes, lookupLane } from "Reporting/CFDReport/Pickers/SwimlanePickerOptions";
import * as Resources from "Reporting/CFDReport/Resources";
import { ObservableValue } from "VSS/Core/Observable";
import { ICancelablePromise } from "VSS/Core/Util/Promise";
import { format } from "VSS/Core/Util/String";
import { NoOpComponent } from "VSS/Platform/Components/Layout";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IButtonProps } from "VSSUI/Button";
import { FormItem } from "VSSUI/FormItem";
import { IListBoxItem } from "VSSUI/ListBox";
import { TextField, TextFieldWidth } from "VSSUI/TextField";
import { addDays } from "WidgetCommon/DateUtils/DateUtils";
import { GenericSelectionDropdown } from "Widgets/Configs/Common/GenericSelectionDropdown";
import { CumulativeFlowDiagramSettings } from "Widgets/CumulativeFlowDiagram/CumulativeFlowDiagramContracts";
import { getTeachingBubblesData } from "./CfdReportTeachingBubbleHelper";
import { CFDReportUserSettings, CFDReportUserSettingsData, getCFDReportSettingsStorageKey } from "./CfdReportUserSettings";

export interface CFDReportProps {
    projectId: string;
    teamId: string;
    teamName: string;

    /** The invariant category reference Name used to reliably identify a backlog */
    categoryReferenceName: string;

    /** The user facing name of backlog, used for routing  */
    categoryName: string;

    /** Requests a refresh of current state. */
    lastRefresh?: Date;

    /** Signal for load completion, used for TTI lifecycle of parent page. */
    onLoadCompleted?: () => void;
}

export interface CFDReportState extends BoardsReportState<CumulativeFlowDiagramSettings> {
    daysOptions: IListBoxItem<number>[];
    swimlaneOptions: IListBoxItem<BoardSwimlane>[];
    /** All available column options */
    columnOptions: IListBoxItem<Column>[];
    /** The subset of column options which are currently available under the active swimlane selection. */
    visibleColumnOptions: IListBoxItem<Column>[];
    board?: Board;
    isCustomRollingPeriod?: boolean;
    customRollingPeriodValidationMessage: string | null;
    showTeachingBubble?: boolean;
    hasCustomUserSettings?: boolean;
}

export class CFDReport extends BoardsReport<CFDReportProps, CumulativeFlowDiagramSettings, CFDReportState> {
    public static readonly defaultRollingDuration = 30;
    private textFieldValue: ObservableValue<string> = new ObservableValue<string>("");
    private reportGuidedTourUtility: ReportGuidedTourUtility;

    private boardCanceleablePromise: ICancelablePromise<Board | undefined>;
    private columnsCanceleablePromise: ICancelablePromise<IListBoxItem<Column>[]>;
    private swimlanesCanceleablePromise: ICancelablePromise<IListBoxItem<BoardSwimlane>[]>;

    constructor(props: CFDReportProps, context?: IVssComponentContext) {
        super(props, context);
        this.state = this.getDefaultState();
    }
    public getWidgetContributionId(): string {
        return "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.CumulativeFlowDiagramWidget";
    }

    public getReportTelemetryName(): string {
        return "CFDReport";
    }

    public getReportTitle(): string {
        return Resources.ReportTitle;
    }

    public renderTeachingBubble(): JSX.Element {
        return <> {this.reportGuidedTourUtility.render(this.state.showTeachingBubble)} </>;
    }

    public getWidgetName(): string {
        const selectedSwimlane = lookupLane(this.state.swimlaneOptions, this.state.widgetSettings!.chartDataSettings.boardLane!);
        const hasSwimlane = selectedSwimlane.data != null && selectedSwimlane.data.LaneId != null; // True if any concrete swimlane, including default, is selected.
        const teamName = this.props.teamName;
        const name = hasSwimlane
            ? format(Resources.WidgetTitleWithSwimlaneFormat, teamName, this.props.categoryName, selectedSwimlane.data!.LaneName)
            : format(Resources.WidgetTitleWithoutSwimlaneFormat, teamName, this.props.categoryName);
        return name;
    }

    public initializeOnRender(): void {
        this.reportGuidedTourUtility = new ReportGuidedTourUtility(this.context.pageContext, getTeachingBubblesData(), this.onBubbleDismiss);
    }

    public getTitleBlock(): JSX.Element {
        const showIcon = this.isReady() && !this.isInAbnormalState() && this.isConfigurationValid();
        return (
            <>
                {super.getTitleBlock(
                    getInfoIcon(this.reportGuidedTourUtility.titleBubbleCallback, this.toggleTeachingBubble, showIcon, this.getReportTitle())
                )}
            </>
        );
    }

    public decorateWidgetContent(widgetContent: JSX.Element | null): JSX.Element {
        return <div ref={this.reportGuidedTourUtility.chartBubbleCallback}>{widgetContent}</div>;
    }

    public getFilterItems(): JSX.Element {
        const chartDataSettings = this.state.widgetSettings!.chartDataSettings;

        // TODO: Reconcile load to handle custom settings which aren't in the picker options.
        const timePeriodSelection = getDaysItem(this.state.daysOptions, chartDataSettings.timePeriod.settings as number);

        const selectedSwimlane = lookupLane(this.state.swimlaneOptions, this.state.widgetSettings!.chartDataSettings.boardLane!);

        return (
            <>
                <FormItem label={Resources.RollingPeriodLabel}>
                    <GenericSelectionDropdown
                        className="days-picker"
                        selectedId={this.state.isCustomRollingPeriod ? customRollingPeriodId : timePeriodSelection}
                        items={this.state.daysOptions}
                        onSelect={this.onDaysSelect}
                    />
                </FormItem>
                {this.state.isCustomRollingPeriod && (
                    <FormItem
                        label={Resources.CustomRollingPeriodLabel}
                        className="custom-rolling-period-form-item"
                        message={this.state.customRollingPeriodValidationMessage}
                        error={this.state.customRollingPeriodValidationMessage !== null}
                    >
                        <TextField
                            ariaLabel={Resources.CustomRollingPeriod}
                            value={this.textFieldValue}
                            onChange={this.onCustomRollingPeriodChanged}
                            width={TextFieldWidth.auto}
                            className={"custom-rolling-period-text-field"}
                            inputClassName={"custom-rolling-period-text-field-input"}
                        />
                    </FormItem>
                )}
                <FormItem label={Resources.SwimlaneLabel}>
                    <GenericSelectionDropdown
                        className="swimlane-picker"
                        selectedId={selectedSwimlane.id}
                        items={this.state.swimlaneOptions}
                        onSelect={this.onLaneSelect}
                    />
                </FormItem>
                <div ref={this.reportGuidedTourUtility.filterBubbleCallback}>
                    <FormItem label={Resources.ColumnOptionsLabel}>
                        <ColumnPicker
                            items={this.state.visibleColumnOptions}
                            onSelect={this.onColumnsSelect}
                            selectedIds={chartDataSettings.desiredBoardColumnIds!}
                        />
                    </FormItem>
                </div>
                <ResetElement onClick={this.resetSettings} isEnabled={this.state.hasCustomUserSettings} />
            </>
        );
    }

    public getKnownWarningComponent(): JSX.Element | null {
        if (this.state.knownWarning === CommonResources.UnmaterializedBoardDetail) {
            return <UnmaterializedBoardMessage />;
        }
        return null;
    }

    public componentDidMount() {
        resetQueryCache(this.context.pageContext);
        this.initializeReportState();
    }

    public componentDidUpdate(prevProps: CFDReportProps) {
        if (JSON.stringify(this.props) !== JSON.stringify(prevProps)) {
            if (this.props.lastRefresh !== prevProps.lastRefresh) {
                resetQueryCache(this.context.pageContext);
            }

            this.setState(this.getDefaultState());
            this.initializeReportState();
        }
    }

    public async initializeReportState() {
        this.cancelDataPromises();

        try {
            const userSettings: CFDReportUserSettingsData = await CFDReportUserSettings.load(
                this.context.pageContext,
                this.props.teamId,
                this.props.categoryReferenceName
            );

            const hasUserDismissedBubble = await hasUserSeenBubble(this.context.pageContext, getCFDReportSettingsStorageKey());

            this.boardCanceleablePromise = this.trackPromise(
                getBoard(this.context.pageContext, this.props.projectId, this.props.teamId, this.props.categoryReferenceName)
            );

            const board = await this.boardCanceleablePromise.promise;

            if (board == null || board.BoardId == null) {
                const warningMessage = CommonResources.UnmaterializedBoardDetail;
                this.setState({
                    knownWarning: warningMessage
                });
                publishEmptyReportTelemetry(this.context.pageContext, this.getReportTelemetryName(), warningMessage);
                return;
            }

            const days = userSettings.rollingDuration ? userSettings.rollingDuration : CFDReport.defaultRollingDuration;
            this.swimlanesCanceleablePromise = this.trackPromise(getSwimlanes(this.context.pageContext, this.props.projectId, board.BoardId));

            const startDate = addDays(new Date(), -days);
            this.columnsCanceleablePromise = this.trackPromise(getColumns(this.context.pageContext, this.props.projectId, board.BoardId, startDate));
            const [swimlanes, columns] = await Promise.all([this.swimlanesCanceleablePromise.promise, this.columnsCanceleablePromise.promise]);

            // If not in user selection, we get the null "All" Lanes option, which does not have a concrete lane definition. Lanes only exist with names using actual strings.
            const savedSwimlaneName = userSettings.boardLane ? userSettings.boardLane : null;
            const activeSwimlane = lookupLane(swimlanes, savedSwimlaneName);

            const visibleColumns = this.generateVisibleColumns(activeSwimlane, columns);
            const desiredColumnIds = this.generateDesiredColumnIds(userSettings.boardColumnIds, visibleColumns, columns);

            const widgetSettings = this.assembleSettings(board, activeSwimlane.data!, days, desiredColumnIds); // TODO: Prioritize saved defaults or URL params driven settings

            const daysOptions = getDaysItems();

            const isCustomRollingPeriod = this.isCustomRollingPeriod(daysOptions, days);

            this.setState({
                daysOptions: daysOptions,
                swimlaneOptions: swimlanes,
                columnOptions: columns,
                visibleColumnOptions: visibleColumns,
                widgetSettings: widgetSettings,
                isCustomRollingPeriod: isCustomRollingPeriod,
                showTeachingBubble: !hasUserDismissedBubble,
                hasCustomUserSettings: JSON.stringify(userSettings) !== "{}"
            });

            // Set user's custom iteration value to text field here
            if (isCustomRollingPeriod) {
                this.textFieldValue.value = this.state.widgetSettings!.chartDataSettings.timePeriod.settings.toString();
            }

            publishReportTelemetry(this.context.pageContext, this.getReportTelemetryName(), {
                rollingPeriod: widgetSettings.chartDataSettings.timePeriod.settings, // Date duration,
                boardLane: widgetSettings.chartDataSettings.boardLane!, // Selected Lane (if any)
                // TODO: Telemetry on CFD Board column selections (not supported yet)
                columnCount: widgetSettings.chartDataSettings.desiredBoardColumnIds && widgetSettings.chartDataSettings.desiredBoardColumnIds.length
            });
        } catch (e) {
            if (e.isCanceled) {
                return; // Do nothing if the promises were cancelled
            }
            this.handleCommonInitError(e);
        } finally {
            if (this.props.onLoadCompleted) {
                this.props.onLoadCompleted();
            }
        }
    }

    public getDefaultState(): CFDReportState {
        return {
            board: undefined,
            daysOptions: [],
            swimlaneOptions: [],
            columnOptions: [],
            visibleColumnOptions: [],
            customRollingPeriodValidationMessage: null,
            ...super.getDefaultState()
        };
    }

    public isConfigurationValid(): boolean {
        return this.state.customRollingPeriodValidationMessage === null;
    }

    protected getBackButtonProps(): IButtonProps | undefined {
        return {
            onClick: () => {
                const destination = getBoardsReportUrl(this.context.pageContext, this.props.categoryName);
                const recordHistoryEntry = true;
                FastPageSwitch(this.context.pageContext, destination, recordHistoryEntry);
            }
        };
    }

    private cancelDataPromises(): void {
        if (this.boardCanceleablePromise) {
            this.boardCanceleablePromise.cancel();
        }

        if (this.swimlanesCanceleablePromise) {
            this.swimlanesCanceleablePromise.cancel();
        }

        if (this.columnsCanceleablePromise) {
            this.columnsCanceleablePromise.cancel();
        }
    }

    private generateVisibleColumns(activeSwimlane: IListBoxItem<BoardSwimlane>, columns: IListBoxItem<Column>[]): IListBoxItem<Column>[] {
        const allowFirstColumn = activeSwimlane.data != null && (activeSwimlane.data.IsDefaultLane || activeSwimlane.data.LaneId == null); // If we are on the default lane, or "All" lanes, the first column is allowed to be active.
        const visibleColumns = [...columns];
        if (!allowFirstColumn) {
            visibleColumns.pop();
        }
        return visibleColumns;
    }

    private generateDesiredColumnIds(boardColumnIds: string[], visibleColumns: IListBoxItem<Column>[], columns: IListBoxItem<Column>[]): string[] {
        let desiredColumnIds;
        if (boardColumnIds !== undefined) {
            desiredColumnIds = boardColumnIds;
        } else {
            desiredColumnIds = visibleColumns.map(cols => {
                return cols.id;
            });

            if (columns.length === visibleColumns.length) {
                desiredColumnIds.pop(); // Omit the first column as a default selection, where it is available.
            }
        }
        return desiredColumnIds;
    }

    private resetSettings = async (): Promise<void> => {
        // wait to clear out previously saved settings, and re-initialize the report.
        await CFDReportUserSettings.reset(this.context.pageContext, this.props.teamId, this.props.categoryReferenceName);
        this.initializeReportState();
    };

    private onDaysSelect = (rollingPeriod: IListBoxItem<number>): void => {
        const isCustomRollingPeriodSelected = rollingPeriod.id === customRollingPeriodId;

        if (isCustomRollingPeriodSelected) {
            this.textFieldValue.value = this.state.widgetSettings!.chartDataSettings.timePeriod.settings.toString();
            this.setState({ customRollingPeriodValidationMessage: null });
        } else {
            const newSettings = { ...this.state.widgetSettings } as CumulativeFlowDiagramSettings;
            newSettings.chartDataSettings.timePeriod.settings = rollingPeriod.data!;
            this.setUserSelection(newSettings);
        }

        this.setState({ isCustomRollingPeriod: isCustomRollingPeriodSelected });
    };

    private onLaneSelect = (swimlane: IListBoxItem<BoardSwimlane>): void => {
        const newSettings = { ...this.state.widgetSettings } as CumulativeFlowDiagramSettings;
        const visibleColumns = this.generateVisibleColumns(swimlane, this.state.columnOptions);
        const desiredColumnIds = this.generateDesiredColumnIds(
            newSettings.chartDataSettings.desiredBoardColumnIds!,
            visibleColumns,
            this.state.columnOptions
        );
        newSettings.chartDataSettings.boardLane = swimlane.data!.LaneName;
        newSettings.chartDataSettings.desiredBoardColumnIds = desiredColumnIds;
        this.setState({ visibleColumnOptions: visibleColumns });
        this.setUserSelection(newSettings);
    };

    private onColumnsSelect = (columns: IListBoxItem<Column>[]): void => {
        const newSettings = { ...this.state.widgetSettings } as CumulativeFlowDiagramSettings;
        newSettings.chartDataSettings.desiredBoardColumnIds = columns.map(item => {
            return item.data!.ColumnId;
        });
        this.setUserSelection(newSettings);
    };

    private onCustomRollingPeriodChanged = (_event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string): void => {
        // Ensure value entered by user is on display in every case
        // Only integer values are allowed
        const newValue = value.replace(/\D/, "");
        this.textFieldValue.value = newValue;
        this.validateAndSaveCustomRollingPeriodInput(newValue);
    };

    // Handle custom number of days input in 4 cases : empty textfield , valid number , 0 and not a number
    private validateAndSaveCustomRollingPeriodInput(value: string) {
        const customRollingPeriod = Number(value);
        const minimumDays = 14;
        const maximumDays = 180;

        if (value !== "" && customRollingPeriod >= minimumDays && customRollingPeriod <= maximumDays) {
            this.setState({ customRollingPeriodValidationMessage: null });
            const newSettings = { ...this.state.widgetSettings! };
            newSettings.chartDataSettings.timePeriod.settings = customRollingPeriod;
            this.setUserSelection(newSettings);
        } else {
            this.setState({
                customRollingPeriodValidationMessage: format(CommonResources.ValueMustBeWithinRange, minimumDays, maximumDays)
            });
        }
    }

    // Is any value in the default rolling period options mapping to the rollin period dats in the state obtained from saved user settings
    private isCustomRollingPeriod(daysOptions: IListBoxItem<number>[], days: number): boolean {
        return !daysOptions.some(item => {
            return item.data != null && item.data === days;
        });
    }

    private assembleSettings(board: Board, swimlane: BoardSwimlane, days: number, desiredColumnIds: string[]): CumulativeFlowDiagramSettings {
        const customSettingsObject: CumulativeFlowDiagramSettings = {
            chartDataSettings: {
                board: board.BoardId!,
                boardLane: swimlane.LaneName,
                includeFirstBoardColumn: false,
                desiredBoardColumnIds: desiredColumnIds,
                project: this.props.projectId,
                team: this.props.teamId,
                timePeriod: {
                    identifier: "RollingPeriod",
                    settings: days
                }
            },
            themeName: "Blue"
        };
        return customSettingsObject;
    }

    private toggleTeachingBubble = (): void => {
        this.setState({
            showTeachingBubble: !this.state.showTeachingBubble
        });
    };

    private onBubbleDismiss = (): void => {
        this.setState({
            showTeachingBubble: false
        });
    };

    private setUserSelection(newSettings: CumulativeFlowDiagramSettings) {
        CFDReportUserSettings.save(this.context.pageContext, newSettings, this.props.teamId, this.props.categoryReferenceName);
        this.setState({ widgetSettings: newSettings, hasCustomUserSettings: true });
    }
}
VssComponent.register("CFDReport", CFDReport);
VssComponent.register("CFDReportPreloader", NoOpComponent);
